import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { GridOptions } from 'ag-grid';
import { PayorsService } from 'app/pages/payors/payors.service';
import { VehiclesService } from 'app/pages/vehicles/vehicles.service';
import { DriversService } from 'app/pages/drivers/drivers.service';
import { UsersService } from 'app/pages/users/users.service';
import { SkillsService } from 'app/pages/autorouting/skills.service';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { ToastrAlertHelper } from 'app/core/helpers/toastr-alert-helper-service';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';

@Component({
  selector: 'app-add-new-skill',
  templateUrl: './add-new-skill.component.html',
  styleUrls: ['./add-new-skill.component.scss']
})
export class AddNewSkillComponent implements OnInit, OnChanges {

  @Input('skillId') skillId = 0;
  @Output('cancel') cancel = new EventEmitter<any>();
  @Output('success') success = new EventEmitter<any>();
  loaded = false;
  skillForm: FormGroup;
  providers = [];
  selectedVehicles = [];
  selectedDrivers = [];
  selectedPayors = [];

  vehicleGrid: GridOptions;
  driverGrid: GridOptions;
  payorGrid: GridOptions;

  constructor(
    private fb: FormBuilder,
    private payorService: PayorsService,
    private vehicleService: VehiclesService,
    private driverService: DriversService,
    private userService: UsersService,
    private service: SkillsService,
    private loading: T2LoadingService,
    private toastrAlertHelper: ToastrAlertHelper,
    private sweetAlertHelper: SweetAlertHelper,
    ) {
    this.skillForm = this.fb.group({
      skillid: 0,
      description: '',
      providerid: 19,
      depots: [[]]
    });

    this.vehicleGrid = <GridOptions>{};
    this.driverGrid = <GridOptions>{};
    this.payorGrid = <GridOptions>{};

    this.vehicleGrid.columnDefs = this.getVehicleGridColumns();
    this.driverGrid.columnDefs = this.getDriverGridColumns();
    this.payorGrid.columnDefs = this.getPayorGridColumns();
    this.vehicleGrid.getRowNodeId = (data) => {
      return data.vehicleid;
    };

    this.driverGrid.getRowNodeId = (data) => {
      return data.userprofileid;
    };

    this.payorGrid.getRowNodeId = (data) => {
      return data.payorid;
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
   if (changes['skillId']) {
    this.loadInformation();
   }
  }

  ngOnInit() {
    this.getAllProviders();
    this.skillForm.get('depots').valueChanges.subscribe(
      () => {
        this.getAllVehicles();
      }
    )
  }

  loadInformation() {
    if (this.skillId !== 0) {
      this.getSkillByID();
    } else {
      this.configureNewSkill();
    }
  }

  private configureNewSkill() {
    this.skillForm.patchValue({
      skillid: 0,
      description: ''
    });
    this.selectedVehicles = [];
    this.selectedDrivers = [];
    this.selectedPayors = [];
    this.setSelected(this.vehicleGrid, this.selectedVehicles, 'vehicleid');
    this.setSelected(this.driverGrid, this.selectedDrivers, 'mctdriverid');
    this.setSelected(this.payorGrid, this.selectedPayors, 'payorid');
  }

  /**
   * this execute when grid is ready
   * @param event
   */
  onGridReady(event, gridName) {
   // event.api.sizeColumnsToFit();

    switch (gridName) {
      case 'payors':
        this.getAllPayors();
        break;
      case 'vehicles':
        this.getAllVehicles();
        break;
      case 'drivers':
        this.getAllDriver();
        break;
      default:
        break;
    }
  }

  /**
   * this get all vehicles
   */
  private getAllVehicles() {
    this.loading.show();
    const providerid = this.skillForm.get('providerid').value;
    const depots = this.skillForm.get('depots').value;
    this.vehicleService.getVehicleByCompanyAndDepots(providerid, depots.map(i => i.depotid))
    .finally(() => {
      this.loading.hiden();
    }).subscribe(
      vehicles => {
        this.vehicleGrid.api.setRowData(vehicles);

        if (this.skillId) {
          this.setSelected(this.vehicleGrid, this.selectedVehicles, 'vehicleid');
        }
      }
    )
  }

  /**
   * this get all drivers
   */
  private getAllDriver() {
    this.loading.show();
    const providerid = this.skillForm.get('providerid').value;
    this.driverService.getDriversProviderAllByProvider(providerid)
    .finally(() => {
      this.loading.hiden();
    }).subscribe(
      drivers => {
        this.driverGrid.api.setRowData(drivers);

        if (this.skillId) {
          this.setSelected(this.driverGrid, this.selectedDrivers, 'mctdriverid');
        }
      }
    )
  }

  /**
   * this get all payors
   */
  private getAllPayors() {
    this.loading.show()
    this.payorService.getPayorsAll()
    .finally(() => {
      this.loading.hiden();
    }).subscribe(
      payors => {
        this.payorGrid.api.setRowData(payors);

        if (this.skillId) {
          this.setSelected(this.payorGrid, this.selectedPayors, 'payorid');
        }
      }
    )
  }

  /**
   * this get all providers
   */
  private getAllProviders() {
    this.userService.getAllProviders().subscribe(
      provs => {
        this.providers = provs;
      }
    );
  }

  /**
   * this execute when user change provider
   */
  onProviderChange() {
    this.getAllDriver();
    this.getAllVehicles();
  }

  /**
   * this save the new skill
   */
  saveSkill() {
   this.loading.show();
   this.selectedDrivers = this.getSelected(this.driverGrid, 'userprofileid', 'mctdriverid');
   this.selectedVehicles = this.getSelected(this.vehicleGrid, 'vehicleid');
   this.selectedPayors = this.getSelected(this.payorGrid, 'payorid');

   const data = this.skillForm.value;
   data['payorskills'] = this.selectedPayors;
   data['mctdriverskills'] = this.selectedDrivers;
   data['vehicleskills'] = this.selectedVehicles;
   const method = this.skillId ? 'updateSkill' : 'saveSkill';
   this.service[method](data).subscribe(
     res => {
       this.sweetAlertHelper.createCustomAlert({ title: 'Success!', text: res.message, type: 'success' });
       this.success.emit();
       this.loading.hiden();
     },
     error => {
       this.sweetAlertHelper.captureException(error)
         .then(() => this.loading.hiden());
     }
   );
  }

  /**
   * this close the new skill view
   */
  cancelAction() {
    this.cancel.emit();
  }

  /**
   * this get selected items from a grid
   * @param grid grid
   * @param key key (table id)
   */
  private getSelected(grid: GridOptions, key: string, objKey?) {
    const ids = [];
    grid.api.forEachNodeAfterFilter((node) => {
      if (node.isSelected()) {
        let item = {};
        item[objKey || key] = node.data[key];
        ids.push(item);
      }
    });
    return ids;
  }

  /**
   * this retrieve skill data from serve
   */
  private getSkillByID() {
    this.service.getSkillByID(this.skillId).subscribe(
      skill => {
        this.skillForm.patchValue(skill);
        this.selectedDrivers = skill.mctdriverskills;
        this.selectedVehicles = skill.vehicleskills;
        this.selectedPayors = skill.payorskills;

        this.getAllVehicles();
        this.getAllDriver();
        this.getAllPayors();
      }, (error) => this.sweetAlertHelper.captureException(error)
    );
  }

  onCloseCard(event) {
    if (!event.isOpen){
      this.cancel.emit();
    }
  }

  /**
   * this set selected items to a grid
   * @param grid grid to set selected
   * @param data selected data
   */
  setSelected(grid: GridOptions, data, key: string) {
    if (grid.api) {
      grid.api.deselectAll()
    }
    data.forEach(item => {
      const node = grid.api.getRowNode(item[key]);
      if(node) {
        node.setSelected(true);
      }
    })
  }

  /**
   * this get vehicle grid def
   */
  private getVehicleGridColumns() {
    return [{
      headerName: 'Check',
      colId: 'check',
      width: 30,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      suppressFilter: true,
    }, {
      headerName: 'Veh ID',
      width: 100,
      field: 'vehicleid',
      colId: 'vehicleid'
    }];
  }

  /**
   * this get driver column def
   */
  private getDriverGridColumns() {
    return [{
      headerName: 'Check',
      colId: 'check',
      width: 30,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      suppressFilter: true,
    }, {
      headerName: 'Driver Name',
      width: 300,
      field: 'fullname',
      colId: 'driver'
    }]
  }

  /**
   * this get payor column def
   */
  private getPayorGridColumns() {
    return [{
      headerName: 'Check',
      colId: 'check',
      width: 30,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      suppressFilter: true,
    }, {
      headerName: 'Description',
      width: 300,
      field: 'description',
      colId: 'description'
    }]
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/autorouting/components/skills/components/add-new-skill/add-new-skill.component.ts